import { useEffect, useRef, useState } from 'react';

export interface Country {
  id: string;
  iso2: string;
  name: string;
  flagEmoji: string;
  phoneCode: string;
}

interface CountrySelectProps {
  countries: Country[];
  value: string;
  onChange: (code: string) => void;
  placeholder?: string;
  hidePhoneCode?: boolean;
}

export function CountrySelect({
  countries,
  value,
  onChange,
  placeholder = 'Select country',
  hidePhoneCode = false,
}: CountrySelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const selected = countries.find((c) => c.iso2 === value);

  const filtered = search
    ? countries.filter(
        (c) =>
          c.name.toLowerCase().includes(search.toLowerCase()) ||
          c.iso2.toLowerCase().includes(search.toLowerCase()),
      )
    : countries;

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-3 py-2 border border-surface-200 rounded-lg text-sm bg-white text-left cursor-pointer hover:border-brand-400 transition"
      >
        {selected ? (
          <>
            <span className="text-base">{selected.flagEmoji}</span>
            <span className="text-ink-900 flex-1 truncate">{selected.name}</span>
            {!hidePhoneCode && <span className="text-ink-500 text-xs">{selected.phoneCode}</span>}
          </>
        ) : (
          <span className="text-ink-500">{placeholder}</span>
        )}
        <svg className="w-4 h-4 text-ink-500 flex-none ml-auto" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full max-h-60 overflow-auto bg-white border border-surface-200 rounded-lg shadow-lg">
          <div className="sticky top-0 bg-white p-2 border-b border-surface-200">
            <input
              autoFocus // eslint-disable-line jsx-a11y/no-autofocus -- autoFocus needed for country search dropdown
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search countries..."
              className="w-full px-2.5 py-1.5 border border-surface-200 rounded-md text-sm outline-none focus:border-brand-400"
            />
          </div>
          {filtered.map((country) => (
            <button
              key={country.iso2}
              type="button"
              onClick={() => {
                onChange(country.iso2);
                setOpen(false);
                setSearch('');
              }}
              className={'w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-surface-50 transition ' + (country.iso2 === value ? 'bg-brand-50 text-brand-700' : 'text-ink-900')}
            >
              <span className="text-base">{country.flagEmoji}</span>
              <span className="flex-1 truncate">{country.name}</span>
              {!hidePhoneCode && <span className="text-ink-500 text-xs">{country.phoneCode}</span>}
            </button>
          ))}
          {filtered.length === 0 && (
            <div className="px-3 py-4 text-center text-sm text-ink-500">No countries found</div>
          )}
        </div>
      )}
    </div>
  );
}
